import { useState, useEffect } from "react";
import apiClient from "../../../shared/API/apiClient";

const validate = (form) => {
  const errors = {};
  if (!form.year_label.trim())
    errors.year_label = "Reporting Year is required";
  if (!form.start_date)
    errors.start_date = "Start Date is required";
  if (!form.end_date)
    errors.end_date = "End Date is required";
  if (
    form.start_date &&
    form.end_date &&
    new Date(form.start_date) > new Date(form.end_date)
  )
    errors.end_date = "End Date cannot be before Start Date";
  return errors;
};

const ReportingYearEditModal = ({ data, onClose, onSave }) => {
  const [form, setForm] = useState({
    year_label: "",
    start_date: "",
    end_date: "",
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // ✅ Pre-fill form
  useEffect(() => {
    if (data) {
      setForm({
        year_label: data.year_label || "",
        start_date: data.start_date?.slice(0, 10) || "",
        end_date: data.end_date?.slice(0, 10) || "",
      });
    }
  }, [data]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleBlur = (field) => () => {
    const errs = validate(form);
    if (errs[field]) {
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  // ✅ Submit
  const handleSubmit = async () => {
  const errs = validate(form);
  if (Object.keys(errs).length > 0) {
    setErrors(errs);
    return;
  }

  try {
    setSaving(true);

    const payload = {
      id: data.id, // ✅ important
      year_label: form.year_label.trim(),
      start_date: form.start_date,
      end_date: form.end_date,
    };

    const res = await apiClient.post(
      "/api/reportingyear-edit",
      payload
    );

    if (!(res.data?.code === 200 || res.data?.success)) {
      throw new Error(res.data?.message || "Update failed");
    }

    onSave(res.data?.message || "Reporting year updated successfully");
  } catch (err) {
    console.error("Edit error:", err);
    setErrors({
      year_label:
        err.response?.data?.message ||
        err.message ||
        "Something went wrong",
    });
  } finally {
    setSaving(false);
  }
};

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-box"
        style={{ maxWidth: "520px" }}
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header */}
        <div className="modal-header">
          <div className="modal-title">Edit Reporting Year</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="modal-body">

          {/* Reporting Year */}
          <div className="org-form-field">
            <label className="org-field-label">
              Reporting Year <span className="required">*</span>
            </label>
            <input
              className={`org-field-input ${errors.year_label ? "org-field-input--error" : ""}`}
              value={form.year_label}
              onChange={handleChange("year_label")}
              onBlur={handleBlur("year_label")}
              disabled={data?.is_locked}
            />
            {errors.year_label && (
              <span className="org-field-error">{errors.year_label}</span>
            )}
          </div>

          {/* Start Date */}
          <div className="org-form-field" style={{ marginTop: "16px" }}>
            <label className="org-field-label">
              Start Date <span className="required">*</span>
            </label>
            <input
              type="date"
              className={`org-field-input ${errors.start_date ? "org-field-input--error" : ""}`}
              value={form.start_date}
              onChange={handleChange("start_date")}
              onBlur={handleBlur("start_date")}
              disabled={data?.is_locked}
            />
            {errors.start_date && (
              <span className="org-field-error">{errors.start_date}</span>
            )}
          </div>

          {/* End Date */}
          <div className="org-form-field" style={{ marginTop: "16px" }}>
            <label className="org-field-label">
              End Date <span className="required">*</span>
            </label>
            <input
              type="date"
              className={`org-field-input ${errors.end_date ? "org-field-input--error" : ""}`}
              value={form.end_date}
              onChange={handleChange("end_date")}
              onBlur={handleBlur("end_date")}
              disabled={data?.is_locked}
            />
            {errors.end_date && (
              <span className="org-field-error">{errors.end_date}</span>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            className="org-btn-cancel"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>

          {!data?.is_locked && (
            <button
              className="org-btn-save"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? "Updating..." : "Update"}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default ReportingYearEditModal;